import { Card, CardBody, Col, Row, Table } from "reactstrap";
import {
  Timeline,
  type TimelineStep,
  type TimelineStepState,
} from "components/common/Timeline";
import { Link } from "react-router-dom";
import { DateOnly, DateTime } from "components/common/Date";
import {
  LeaveApplicationStatus,
  leaveApplicationStatusMap,
} from "../constants/leave-application.constants";
import {
  leaveApplicationEmployeeLabel,
  leaveTypeLabel,
} from "../utils/leaveApplicationDisplay";
import { useLeaveApplicationOverview } from "./LeaveApplicationOverviewContext";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { LeaveSummaryPanel } from "../components/LeaveSummaryPanel";

const OverviewTab = () => {
  const { record, summaryKey } = useLeaveApplicationOverview();
  const { can } = useAccess();
  const canReadEmployee = can(Permissions.EMPLOYEE.READ);

  if (!record) {
    return null;
  }

  const history = record.statusHistory ?? [];
  const leaveTimeline: TimelineStep[] =
    history.length > 0
      ? history.map((entry, i) => {
          const isLast = i === history.length - 1;
          const label =
            leaveApplicationStatusMap[entry.status]?.label ?? entry.status;
          const isRejected = entry.status === LeaveApplicationStatus.REJECTED;
          const isCancelled = entry.status === LeaveApplicationStatus.CANCELLED;
          const isApproved = entry.status === LeaveApplicationStatus.APPROVED;
          const state: TimelineStepState = isLast
            ? isRejected || isCancelled
              ? "skipped"
              : isApproved
                ? "done"
                : "active"
            : "done";
          return {
            label,
            timestamp: entry.at,
            by: entry.user
              ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
              : entry.by != null ? `#${entry.by}` : undefined,
            actor: entry.message ?? undefined,
            state,
          };
        })
      : [
          {
            label:
              leaveApplicationStatusMap[record.status]?.label ?? record.status,
            timestamp: record.created_at,
            state: "active" as const,
          },
        ];

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Leave application</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    <tr>
                      <th
                        className="ps-0"
                        scope="row"
                        style={{ width: "180px" }}
                      >
                        Employee :
                      </th>
                      <td className="text-muted">
                        {canReadEmployee ? (
                          <Link
                            to={`/employees/${record.employeeId}`}
                            className="link fw-medium"
                          >
                            {leaveApplicationEmployeeLabel(record)}
                          </Link>
                        ) : (
                          leaveApplicationEmployeeLabel(record)
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Leave type :
                      </th>
                      <td className="text-muted">{leaveTypeLabel(record)}</td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Period :
                      </th>
                      <td className="text-muted">
                        {record?.fromDate ? (
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <span>
                              <DateOnly date={record.fromDate} />
                              {!record.isHalfDay &&
                              record?.toDate &&
                              record.toDate !== record.fromDate ? (
                                <>
                                  {" "}
                                  – <DateOnly date={record.toDate} />
                                </>
                              ) : null}
                            </span>
                            {record.isHalfDay ? (
                              <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                                Half Day
                              </span>
                            ) : null}
                            {record.isHalfDay || record.totalDays != null ? (
                              <span className="badge bg-light text-muted border">
                                {record.isHalfDay || record.totalDays === 0.5
                                  ? "0.5 days"
                                  : `${record.totalDays} ${record.totalDays === 1 ? "day" : "days"}`}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {record?.reason?.trim() ? (
                <div className="pt-3 mt-4">
                  <h6 className="mb-3 text-uppercase">Reason</h6>
                  <p className="text-muted mb-0">{record.reason}</p>
                </div>
              ) : null}

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  {record?.created_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={record.created_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                  {record?.updated_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Last updated
                        </p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={record.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      <Col lg={4}>
        <Card>
          <CardBody>
            <h6 className="mb-4 text-uppercase text-muted">Timeline</h6>
            <Timeline steps={leaveTimeline} />
          </CardBody>
        </Card>

        <div className="rounded p-3 mt-0" style={{ background: "var(--vz-secondary-bg)" }}>
          <p className="text-uppercase fw-semibold text-muted mb-3" style={{ fontSize: "10px", letterSpacing: "0.6px" }}>
            Leave summary
          </p>
          <LeaveSummaryPanel
            fromDate={record.fromDate ?? null}
            toDate={record.toDate ?? null}
            employeeId={record.employeeId}
            refreshKey={summaryKey}
          />
        </div>
      </Col>
    </Row>
  );
};

export default OverviewTab;
